<template>
  <div class="cc" data-testid="campaign-chat">
    <div ref="logEl" class="cc-log">
      <!-- Intro -->
      <div class="cc-msg cc-assistant">
        <p>Goal: <b>{{ objectiveLabel }}</b><span v-if="campaign?.target_value"> · target {{ fmt(campaign.target_value) }}</span>.</p>
        <p v-if="focusLabel" class="cc-sub">Focused on <b>{{ focusLabel }}</b> — chosen from your site &amp; capabilities.</p>
        <p v-else class="cc-sub">Just generate and I'll pick the best capability &amp; topic from your site to hit this goal — or steer me below first.</p>
      </div>

      <!-- User steer history -->
      <template v-for="m in messages" :key="m.id">
        <div v-if="m.role === 'user'" class="cc-msg cc-user"><p>{{ m.text }}</p></div>
        <div v-else class="cc-msg cc-assistant"><p>{{ m.text }}</p></div>
      </template>

      <!-- Live state card -->
      <div v-if="pendingPosts.length" class="cc-msg cc-assistant">
        <p>I drafted <b>{{ pendingPosts.length }}</b> post{{ pendingPosts.length > 1 ? 's' : '' }}{{ isRecalibrated ? ' — improved from your last results' : '' }}. Review and approve:</p>
        <div class="cc-drafts">
          <article v-for="p in pendingPosts" :key="p.id" class="cc-draft" @click="$emit('select-post', p)">
            <div class="cc-draft-head">
              <span class="cc-chan" :data-platform="p.channel">{{ channelLabel(p.channel) }}</span>
              <span v-if="p.format && p.format !== 'text'" class="cc-format">{{ p.format }}</span>
            </div>
            <p class="cc-draft-content">{{ p.content }}</p>
          </article>
        </div>
        <div class="cc-approve-row">
          <label class="cc-inline">starts in
            <input v-model.number="startInDays" type="number" min="0" max="30" class="cc-mini" /> day(s)
          </label>
          <button class="cc-btn cc-btn-primary" :disabled="scheduling" data-testid="cc-approve" @click="$emit('approve', { waveId: pendingWaveId, startInDays })">
            {{ scheduling ? 'Scheduling…' : 'Approve & schedule' }}
          </button>
        </div>
      </div>

      <div v-else-if="activeCycle" class="cc-msg cc-assistant">
        <p v-if="activeCycle.status === 'scheduled'">Scheduled — publishing over the next days. I'll watch performance.</p>
        <p v-else>Watching performance{{ activeReport ? '' : ' — collect results when a couple of days have passed' }}.</p>
        <p v-if="activeReport && activeReport.suggestion.note" class="cc-sub">{{ activeReport.suggestion.note }}</p>
        <p v-if="recSummary" class="cc-rec">Recommendation: {{ recSummary }}</p>
        <button class="cc-btn cc-btn-ghost" :disabled="observing" data-testid="cc-collect" @click="$emit('collect', { waveId: activeCycle.id })">
          {{ observing ? 'Collecting…' : 'Collect results' }}
        </button>
      </div>
    </div>

    <!-- Prompt box (editor-style): steer chips + textarea + send -->
    <div class="cc-input">
      <div class="cc-prompt-wrap">
        <div class="cc-chips" data-testid="cc-steers">
          <button v-for="s in steers" :key="s" type="button" class="cc-chip" @click="input = s">{{ s }}</button>
        </div>
        <label for="cc-steer-input" class="cc-sr">Steer the campaign</label>
        <textarea
          id="cc-steer-input"
          v-model="input"
          class="cc-textarea"
          rows="3"
          data-testid="cc-input"
          :placeholder="inputPlaceholder"
          @keydown.enter.exact.prevent="primary"
        ></textarea>
        <button class="cc-apply-btn" :disabled="generating" data-testid="cc-primary" @click="primary">
          {{ generating ? 'Drafting… (~1–2 min)' : primaryLabel }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'

const props = defineProps({
  campaign: { type: Object, default: null },
  reports: { type: Object, default: () => ({}) },
  generating: { type: Boolean, default: false },
  scheduling: { type: Boolean, default: false },
  observing: { type: Boolean, default: false },
})
const emit = defineEmits(['generate', 'approve', 'collect', 'select-post'])

const input = ref('')
const startInDays = ref(1)
const messages = ref([])
const logEl = ref(null)
let mid = 0

// Preset steers (editor-style quick chips) — click to fill the prompt. These map
// to what the generator actually does: copy tone, angle, length, and emphasis
// (channel mix is steered by the results recommendation, not here).
const steers = ['Punchier tone', 'Shorter', 'Lead with the problem', 'More proof & stats', 'Add a clear CTA', 'More conversational']

const objectiveLabel = computed(() => ({ impressions: 'Impressions', engagement: 'Engagement', followers: 'Followers' }[props.campaign?.objective] || props.campaign?.objective || 'Engagement'))
const focusLabel = computed(() => props.campaign?.topic || props.campaign?.capability_name || '')
const fmt = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString())
const channelLabel = (c) => ({ 'linkedin-page': 'LinkedIn', linkedin: 'LinkedIn', x: 'X', twitter: 'X', instagram: 'Instagram', facebook: 'Facebook', youtube: 'YouTube', tiktok: 'TikTok', threads: 'Threads' }[c] || c)

const waves = computed(() => props.campaign?.waves || [])
const hasSchedule = computed(() => waves.value.length > 0)

const pendingWave = computed(() => {
  for (let i = waves.value.length - 1; i >= 0; i -= 1) {
    if ((waves.value[i].posts || []).some((p) => !p.scheduled_at && p.status !== 'skipped' && p.status !== 'cancelled')) return waves.value[i]
  }
  return null
})
const pendingWaveId = computed(() => pendingWave.value?.id || null)
const isRecalibrated = computed(() => (pendingWave.value?.wave_index || 0) > 0)
const pendingPosts = computed(() => (pendingWave.value?.posts || []).filter((p) => !p.scheduled_at && p.status !== 'skipped' && p.status !== 'cancelled'))

const activeCycle = computed(() => {
  for (let i = waves.value.length - 1; i >= 0; i -= 1) {
    if (['scheduled', 'observing', 'evaluated'].includes(waves.value[i].status)) return waves.value[i]
  }
  return null
})
const activeReport = computed(() => (activeCycle.value ? props.reports[activeCycle.value.id] : null))
const recSummary = computed(() => {
  const s = activeReport.value?.suggestion
  if (!s) return ''
  const parts = []
  if (s.keep?.length) parts.push(`scale ${s.keep.join(', ')}`)
  if (s.drop?.length) parts.push(`drop ${s.drop.join(', ')}`)
  if (s.rewrite?.length) parts.push(`rewrite ${s.rewrite.join(', ')}`)
  return parts.join(' · ')
})

const primaryLabel = computed(() => {
  if (!hasSchedule.value) return 'Generate schedule'
  if (activeReport.value?.suggestion?.ready) return 'Apply recommendation'
  return 'Generate next round'
})
const inputPlaceholder = computed(() => (hasSchedule.value
  ? "Steer the next round — e.g. 'lean into LinkedIn', 'punchier tone', 'add a poll'"
  : "Any steer for the first round? e.g. 'lead with the problem', 'keep it short'"))

function primary() {
  if (props.generating) return
  const text = input.value.trim()
  if (text) messages.value.push({ id: mid++, role: 'user', text })
  emit('generate', text)
  input.value = ''
}

const scrollDown = () => nextTick(() => { if (logEl.value) logEl.value.scrollTop = logEl.value.scrollHeight })
watch(messages, scrollDown, { deep: true })
watch(() => waves.value.map((w) => `${w.id}:${w.status}:${(w.posts || []).length}`).join(','), scrollDown)
watch(() => props.campaign?.id, () => { messages.value = []; input.value = '' })
</script>

<style scoped>
/* Fills its sidebar container seamlessly (no card chrome). */
.cc { display: flex; flex-direction: column; flex: 1; width: 100%; min-height: 0; background: transparent; overflow: hidden; }
.cc-log { flex: 1; overflow-y: auto; padding: 16px; display: flex; flex-direction: column; gap: 12px; }

.cc-msg { max-width: 92%; padding: 12px 14px; border-radius: 14px; font-size: 0.86rem; line-height: 1.55; }
.cc-msg p { margin: 0; }
.cc-msg p + p { margin-top: 6px; }
.cc-assistant { align-self: flex-start; background: var(--glass-bg-active); color: var(--text-primary); border-top-left-radius: 4px; }
.cc-user { align-self: flex-end; background: var(--accent); color: #fff; border-top-right-radius: 4px; }
.cc-sub { color: var(--text-secondary); font-size: 0.8rem; }
.cc-user .cc-sub { color: rgba(255,255,255,0.85); }
.cc-rec { color: var(--accent-light); font-weight: 600; }

.cc-drafts { display: flex; flex-direction: column; gap: 8px; margin-top: 10px; }
.cc-draft { background: var(--glass-bg); border: 1px dashed var(--glass-border-color, rgba(255,255,255,0.16)); border-radius: 10px; padding: 10px 12px; cursor: pointer; transition: border-color 0.15s; }
.cc-draft:hover { border-color: var(--accent); }
.cc-draft-head { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
.cc-chan { font-size: 0.66rem; font-weight: 700; border-radius: 6px; padding: 2px 8px; background: var(--glass-bg-active); color: var(--accent-light); }
.cc-chan[data-platform='linkedin-page'], .cc-chan[data-platform='linkedin'] { background: rgba(56,189,248,0.18); color: #7cc7f5; }
.cc-chan[data-platform='x'], .cc-chan[data-platform='twitter'] { background: rgba(255,255,255,0.12); color: #fff; }
.cc-format { font-size: 0.62rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--text-muted); }
.cc-draft-content { font-size: 0.82rem; color: var(--text-primary); line-height: 1.5; margin: 0; display: -webkit-box; -webkit-line-clamp: 4; -webkit-box-orient: vertical; overflow: hidden; }

.cc-approve-row { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-top: 12px; }
.cc-inline { font-size: 0.78rem; color: var(--text-secondary); display: flex; align-items: center; gap: 6px; }
.cc-mini { width: 50px; padding: 4px 6px; border: var(--glass-border); border-radius: var(--radius-sm); background: var(--glass-bg); color: var(--text-primary); color-scheme: dark; font-family: inherit; }

.cc-btn { border-radius: var(--radius-sm); padding: 9px 14px; font-size: 0.85rem; font-weight: 600; cursor: pointer; border: 1px solid transparent; transition: all 0.15s; white-space: nowrap; }
.cc-btn-primary { background: var(--accent); color: #fff; box-shadow: 0 0 18px var(--accent-glow); }
.cc-btn-primary:hover { background: var(--accent-light); }
.cc-btn-primary:disabled { opacity: 0.55; cursor: not-allowed; box-shadow: none; }
.cc-btn-ghost { background: transparent; border: var(--glass-border); color: var(--text-primary); margin-top: 10px; }
.cc-btn-ghost:hover { background: var(--glass-bg-hover); }
.cc-btn-ghost:disabled { opacity: 0.55; cursor: not-allowed; }

/* Prompt box — mirrors the site editor's chat input. */
.cc-input { padding: 12px; border-top: var(--glass-border); }
.cc-prompt-wrap { display: flex; flex-direction: column; gap: 8px; padding: 8px 10px; background: var(--glass-bg); border: var(--glass-border); border-radius: 10px; }
.cc-chips { display: flex; flex-wrap: wrap; gap: 5px; }
.cc-chip { padding: 5px 10px; border-radius: 20px; font-size: 11px; font-weight: 500; background: var(--glass-bg-active); border: var(--glass-border); color: var(--text-secondary); cursor: pointer; transition: all 0.15s; white-space: nowrap; font-family: inherit; }
.cc-chip:hover { background: var(--accent-soft); border-color: rgba(124,109,250,0.35); color: var(--accent); }
.cc-textarea { width: 100%; resize: none; padding: 10px; background: var(--glass-bg-active); border: var(--glass-border); border-radius: 8px; color: var(--text-primary); font-size: 12px; font-family: inherit; line-height: 1.5; outline: none; display: block; }
.cc-textarea:focus { border: var(--glass-border-hi); background: var(--glass-bg-hover); }
.cc-apply-btn { width: 100%; padding: 9px 12px; background: var(--accent); color: #fff; border: none; border-radius: 8px; font-size: 12px; font-weight: 600; cursor: pointer; box-shadow: 0 0 16px var(--accent-glow); transition: all 0.15s; font-family: inherit; }
.cc-apply-btn:hover { background: #6d5ef0; }
.cc-apply-btn:disabled { opacity: 0.55; cursor: not-allowed; box-shadow: none; }
.cc-sr { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0; }
</style>
